import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Stack,
  InputAdornment
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
  Upload as UploadIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  PersonAdd as PersonAddIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import unifiedApi from '../utils/unifiedApi';
import { FEATURES, useFeature } from '../contexts/FeatureContext';
import { useCanManageLeague } from '../contexts/PermissionsContext';
import SpareAvailabilityTable from '../components/rsvp/SpareAvailabilityTable';
import PlayerSearchAutocomplete from '../components/MatchReport/PlayerSearchAutocomplete.tsx';
import OpenSparesChatButton from '../components/chat/OpenSparesChatButton';

const getMemberName = (member) => member.displayName || `${member.firstName || ''} ${member.lastName || ''}`.trim();

const getMemberEmail = (member) => member.email || '';

function Spares() {
  const { leagueSlug } = useParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [sparePool, setSparePool] = useState(null);
  const [poolMembers, setPoolMembers] = useState([]);
  const [memberSearchTerm, setMemberSearchTerm] = useState('');
  const [memberSortBy, setMemberSortBy] = useState('name');
  const [memberSortDirection, setMemberSortDirection] = useState('asc');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [poolViewTab, setPoolViewTab] = useState(0); // 0 = Members, 1 = Availability

  // Dialogs
  const [addMemberDialog, setAddMemberDialog] = useState(false);
  const [uploadCsvDialog, setUploadCsvDialog] = useState(false);
  const [editMemberDialog, setEditMemberDialog] = useState(false);
  
  // Form states
  const [newMember, setNewMember] = useState({ 
    userTenantId: '', 
    isActive: true 
  });
  const [selectedAddMemberPlayer, setSelectedAddMemberPlayer] = useState(null);
  const [editingMember, setEditingMember] = useState(null);
  const [csvFile, setCsvFile] = useState(null);

  // League data
  const [leagueData, setLeagueData] = useState(null);

  // Permission check - can manage league
  const canManageLeague = useCanManageLeague(leagueData?.id);
  const sparesEnabled = useFeature(FEATURES.SPARES, leagueData?.id);

  const resetNewMemberForm = () => {
    setNewMember({ 
      userTenantId: '', 
      isActive: true 
    });
    setSelectedAddMemberPlayer(null);
  };

  const closeAddMemberDialog = () => {
    setAddMemberDialog(false);
    resetNewMemberForm();
  };

  const handleMemberSort = (field) => {
    setMemberSortBy(field);
    setMemberSortDirection((currentDirection) => (
      memberSortBy === field && currentDirection === 'asc' ? 'desc' : 'asc'
    ));
  };

  const handleSortSelectChange = (value) => {
    const [field, direction] = value.split(':');
    setMemberSortBy(field);
    setMemberSortDirection(direction);
  };

  const filteredPoolMembers = useMemo(() => {
    const normalizedSearchTerm = memberSearchTerm.trim().toLowerCase();
    const members = poolMembers.filter((member) => {
      if (!normalizedSearchTerm) {
        return true;
      }

      const name = getMemberName(member).toLowerCase();
      const email = canManageLeague ? getMemberEmail(member).toLowerCase() : '';
      return name.includes(normalizedSearchTerm) || email.includes(normalizedSearchTerm);
    });

    return [...members].sort((first, second) => {
      const firstValue = memberSortBy === 'email' ? getMemberEmail(first) : getMemberName(first);
      const secondValue = memberSortBy === 'email' ? getMemberEmail(second) : getMemberName(second);
      const comparison = firstValue.localeCompare(secondValue, undefined, { sensitivity: 'base' });
      return memberSortDirection === 'asc' ? comparison : -comparison;
    });
  }, [poolMembers, memberSearchTerm, memberSortBy, memberSortDirection, canManageLeague]);

  const loadLeagueAndPools = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Get league data using slug (endpoint supports both ID and slug)
      const league = await unifiedApi.get(`/api/leagues/${leagueSlug}`);
      setLeagueData(league);

      if (league) {
        setSparePool(await unifiedApi.getLeagueSparePool(league.id));
      }
    } catch (err) {
      console.error('Error loading spares data:', err);
      setError('Failed to load spares data');
    } finally {
      setLoading(false);
    }
  }, [leagueSlug]);

  useEffect(() => {
    loadLeagueAndPools();
  }, [loadLeagueAndPools]);

  const loadPoolMembers = useCallback(async (leagueId) => {
    try {
      const members = await unifiedApi.getSparePoolMembers(leagueId);
      setPoolMembers(members || []);
    } catch (err) {
      console.error('Error loading pool members:', err);
      setError('Failed to load pool members');
    }
  }, []);

  useEffect(() => {
    if (sparePool && sparesEnabled) {
      setMemberSearchTerm('');
      loadPoolMembers(sparePool.leagueId);
    }
  }, [sparePool, sparesEnabled, loadPoolMembers]);

  const handleAddMember = async () => {
    try {
      setError(null);
      const created = await unifiedApi.createSparePoolMember(leagueData.id, newMember);
      setPoolMembers((currentMembers) => [...currentMembers, created]);
      closeAddMemberDialog();
      setSuccess('Spare added successfully');
    } catch (err) {
      console.error('Error adding member:', err);
      setError('Failed to add spare member');
    }
  };

  const handleUpdateMember = async () => {
    try {
      setError(null);
      const updateData = {
        isActive: editingMember.isActive
      };
      const updated = await unifiedApi.updateSparePoolMember(editingMember.id, updateData);
      setPoolMembers(poolMembers.map(m => m.id === updated.id ? updated : m));
      setEditMemberDialog(false);
      setEditingMember(null);
      setSuccess('Spare updated successfully');
    } catch (err) {
      console.error('Error updating member:', err);
      setError('Failed to update spare member');
    }
  };

  const handleDeleteMember = async (memberId) => {
    if (!window.confirm('Are you sure you want to remove this spare?')) {
      return;
    }

    try {
      setError(null);
      await unifiedApi.deleteSparePoolMember(memberId);
      setPoolMembers(poolMembers.filter(m => m.id !== memberId));
      setSuccess('Spare removed successfully');
    } catch (err) {
      console.error('Error deleting member:', err);
      setError('Failed to remove spare member');
    }
  };

  const handleCsvUpload = async () => {
    if (!csvFile) {
      setError('Please select a CSV file');
      return;
    }

    try {
      setError(null);
      const result = await unifiedApi.uploadSparePoolMembersCsv(leagueData.id, csvFile);
      setSuccess(`Successfully imported ${result.members?.length || 0} spares`);
      setUploadCsvDialog(false);
      setCsvFile(null);
      loadPoolMembers(leagueData.id);
    } catch (err) {
      console.error('Error uploading CSV:', err);
      setError('Failed to upload CSV file');
    }
  };

  const openEditDialog = (member) => {
    setEditingMember({...member});
    setEditMemberDialog(true);
  };

  const handleAddMemberPlayerSelected = (player) => {
    setSelectedAddMemberPlayer(player);
    setNewMember((currentMember) => ({ ...currentMember, userTenantId: player.userTenantId }));
  };

  const renderMemberName = (member) => (
    <Link 
      to={`/player/${member.userTenantId}`}
      style={{ textDecoration: 'none', color: 'inherit' }}
    >
      <Typography 
        variant="body2"
        sx={{ 
          '&:hover': { color: 'primary.main', textDecoration: 'underline' },
          cursor: 'pointer',
          wordBreak: 'break-word'
        }}
      >
        {getMemberName(member)}
      </Typography>
    </Link>
  );

  const renderMemberStatus = (member) => (
    <Chip 
      label={member.isActive ? 'Active' : 'Inactive'} 
      color={member.isActive ? 'success' : 'default'}
      size="small"
      clickable={false}
      onClick={() => {/* do nothing */}}
    />
  );

  const renderMemberActions = (member) => canManageLeague && (
    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
      <IconButton
        size="small"
        onClick={() => openEditDialog(member)}
        aria-label={`Edit ${getMemberName(member)}`}
      >
        <EditIcon fontSize="small" />
      </IconButton>
      <IconButton
        size="small"
        color="error"
        onClick={() => handleDeleteMember(member.id)}
        aria-label={`Remove ${getMemberName(member)}`}
      >
        <DeleteIcon fontSize="small" />
      </IconButton>
    </Stack>
  );

  const sortSelectValue = `${memberSortBy}:${memberSortDirection}`;

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!leagueData) {
    return (
      <Box p={3}>
        <Alert severity="error">League not found</Alert>
      </Box>
    );
  }

  if (!sparesEnabled) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3 } }}>
        <Alert severity="info" sx={{ wordBreak: 'break-word' }}>
          {leagueData.name} doesn't use spares.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      {/* Header */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={{ xs: 1, sm: 2 }}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        justifyContent="space-between"
        sx={{ mb: 3 }}
      >
        <Typography variant="h4" className="page-header" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' }, wordBreak: 'break-word' }}>
          Spares - {leagueData.name}
        </Typography>
        <OpenSparesChatButton leagueId={leagueData.id} fullWidth={isMobile} sx={{ flexShrink: 0 }} />
      </Stack>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {sparePool ? (
            <Paper sx={{ p: { xs: 2, sm: 3 } }}>
              {canManageLeague && poolViewTab === 0 && (
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 3 }} justifyContent="flex-end" alignItems={{ xs: 'stretch', sm: 'center' }}>
                  <Button
                    variant="outlined"
                    startIcon={<PersonAddIcon />}
                    onClick={() => setAddMemberDialog(true)}
                    fullWidth={isMobile}
                  >
                    Add Spare
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<UploadIcon />}
                    onClick={() => setUploadCsvDialog(true)}
                    fullWidth={isMobile}
                  >
                    Upload CSV
                  </Button>
                </Stack>
              )}

              {/* Secondary Tabs: Members vs Availability */}
              <Tabs
                value={poolViewTab}
                onChange={(e, newValue) => setPoolViewTab(newValue)}
                sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
              >
                <Tab label="Members" />
                <Tab label="Availability" />
              </Tabs>

              {/* Members Table - shown when poolViewTab === 0 */}
              {poolViewTab === 0 && (
                poolMembers.length > 0 ? (
                  <Stack spacing={2}>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }}>
                      <TextField
                        label={canManageLeague ? 'Filter by name or email' : 'Filter by name'}
                        value={memberSearchTerm}
                        onChange={(event) => setMemberSearchTerm(event.target.value)}
                        fullWidth
                        size="small"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon fontSize="small" />
                            </InputAdornment>
                          )
                        }}
                      />
                      <FormControl size="small" sx={{ minWidth: { xs: '100%', md: 220 } }}>
                        <InputLabel>Sort</InputLabel>
                        <Select
                          value={sortSelectValue}
                          label="Sort"
                          onChange={(event) => handleSortSelectChange(event.target.value)}
                        >
                          <MenuItem value="name:asc">Name A-Z</MenuItem>
                          <MenuItem value="name:desc">Name Z-A</MenuItem>
                          {canManageLeague && <MenuItem value="email:asc">Email A-Z</MenuItem>}
                          {canManageLeague && <MenuItem value="email:desc">Email Z-A</MenuItem>}
                        </Select>
                      </FormControl>
                    </Stack>

                    {filteredPoolMembers.length === 0 ? (
                      <Box sx={{ textAlign: 'center', py: 4 }}>
                        <Typography variant="body1" color="text.secondary">
                          No spares match your filter.
                        </Typography>
                      </Box>
                    ) : isMobile ? (
                      <Stack spacing={1.5}>
                        {filteredPoolMembers.map((member) => (
                          <Paper key={member.id} variant="outlined" sx={{ p: 2 }}>
                            <Stack spacing={1.25}>
                              <Box>
                                {renderMemberName(member)}
                                {canManageLeague && (
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', wordBreak: 'break-all' }}>
                                    {getMemberEmail(member)}
                                  </Typography>
                                )}
                              </Box>
                              <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                                <Box>
                                  <Typography variant="caption" color="text.secondary">Preferred</Typography>
                                  <Typography variant="body2">{member.preferredPosition || '-'}</Typography>
                                </Box>
                                <Box>
                                  <Typography variant="caption" color="text.secondary">Second</Typography>
                                  <Typography variant="body2">{member.secondPreferredPosition || '-'}</Typography>
                                </Box>
                                <Box>
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>Status</Typography>
                                  {renderMemberStatus(member)}
                                </Box>
                              </Stack>
                              {canManageLeague && renderMemberActions(member)}
                            </Stack>
                          </Paper>
                        ))}
                      </Stack>
                    ) : (
                      <TableContainer>
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell>
                                <TableSortLabel
                                  active={memberSortBy === 'name'}
                                  direction={memberSortBy === 'name' ? memberSortDirection : 'asc'}
                                  onClick={() => handleMemberSort('name')}
                                >
                                  Name
                                </TableSortLabel>
                              </TableCell>
                              {canManageLeague && (
                                <TableCell>
                                  <TableSortLabel
                                    active={memberSortBy === 'email'}
                                    direction={memberSortBy === 'email' ? memberSortDirection : 'asc'}
                                    onClick={() => handleMemberSort('email')}
                                  >
                                    Email
                                  </TableSortLabel>
                                </TableCell>
                              )}
                              <TableCell>Preferred Position</TableCell>
                              <TableCell>Second Position</TableCell>
                              <TableCell>Status</TableCell>
                              {canManageLeague && <TableCell align="right">Actions</TableCell>}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {filteredPoolMembers.map((member) => (
                              <TableRow key={member.id}>
                                <TableCell>{renderMemberName(member)}</TableCell>
                                {canManageLeague && (
                                  <TableCell>
                                    <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                                      {getMemberEmail(member)}
                                    </Typography>
                                  </TableCell>
                                )}
                                <TableCell>{member.preferredPosition || '-'}</TableCell>
                                <TableCell>{member.secondPreferredPosition || '-'}</TableCell>
                                <TableCell>{renderMemberStatus(member)}</TableCell>
                                {canManageLeague && (
                                  <TableCell align="right">{renderMemberActions(member)}</TableCell>
                                )}
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </Stack>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="body1" color="text.secondary">
                      No spares in this pool yet.
                    </Typography>
                    {canManageLeague && (
                      <Button
                        variant="contained"
                        startIcon={<PersonAddIcon />}
                        onClick={() => setAddMemberDialog(true)}
                        sx={{ mt: 2 }}
                      >
                        Add First Spare
                      </Button>
                    )}
                  </Box>
                )
              )}

              {/* Availability Table - shown when poolViewTab === 1 */}
              {poolViewTab === 1 && (
                <SpareAvailabilityTable 
                  leagueId={leagueData.id} 
                  canManage={canManageLeague} 
                />
              )}
            </Paper>
      ) : (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            This league doesn't have a spare pool.
          </Typography>
        </Paper>
      )}

      {/* Add Member Dialog */}
      <Dialog open={addMemberDialog} onClose={closeAddMemberDialog} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle>Add Spare to Pool</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1, mb: 2 }}>
            <PlayerSearchAutocomplete
              onPlayerSelected={handleAddMemberPlayerSelected}
              label="Search and select spare"
              placeholder="Type player name or email..."
            />
            {selectedAddMemberPlayer && (
              <Paper variant="outlined" sx={{ mt: 1.5, p: 1.5 }}>
                <Typography variant="body2" fontWeight={600} sx={{ wordBreak: 'break-word' }}>
                  {selectedAddMemberPlayer.name || `${selectedAddMemberPlayer.firstName || ''} ${selectedAddMemberPlayer.lastName || ''}`.trim()}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                  {selectedAddMemberPlayer.email}
                </Typography>
              </Paper>
            )}
          </Box>
          <FormControl fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
              value={newMember.isActive}
              label="Status"
              onChange={(e) => setNewMember({...newMember, isActive: e.target.value})}
            >
              <MenuItem value={true}>Active</MenuItem>
              <MenuItem value={false}>Inactive</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAddMemberDialog}>Cancel</Button>
          <Button onClick={handleAddMember} variant="contained" disabled={!newMember.userTenantId}>
            Add
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Member Dialog */}
      {editingMember && (
        <Dialog open={editMemberDialog} onClose={() => setEditMemberDialog(false)} maxWidth="sm" fullWidth fullScreen={isMobile}>
          <DialogTitle>Edit Spare</DialogTitle>
          <DialogContent>
            <TextField
              margin="dense"
              label="Name"
              type="text"
              fullWidth
              value={editingMember.displayName}
              disabled
              sx={{ mb: 2, mt: 1 }}
            />
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={editingMember.isActive}
                label="Status"
                onChange={(e) => setEditingMember({...editingMember, isActive: e.target.value})}
              >
                <MenuItem value={true}>Active</MenuItem>
                <MenuItem value={false}>Inactive</MenuItem>
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditMemberDialog(false)}>Cancel</Button>
            <Button onClick={handleUpdateMember} variant="contained">
              Update
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Upload CSV Dialog */}
      <Dialog open={uploadCsvDialog} onClose={() => setUploadCsvDialog(false)} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle>Upload Spares CSV</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mt: 1, mb: 2 }}>
            CSV should have columns: FirstName, LastName, Email
          </Alert>
          <input
            type="file"
            accept=".csv"
            onChange={(e) => setCsvFile(e.target.files[0])}
            style={{ marginTop: '16px' }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadCsvDialog(false)}>Cancel</Button>
          <Button onClick={handleCsvUpload} variant="contained" disabled={!csvFile}>
            Upload
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Spares;
